import { IGeofenceOverlaySize } from "@directives/_map/geofences/geofenceoverlay";
import { GeofenceType } from "@enums/GeofenceType";
import { IGeoArea } from "src/app/main/modules/tms/_models/IGeoArea";
import * as angular from "angular";

export class GeoAreaOverlay {
    bounds: google.maps.LatLngBounds;
    overlay: google.maps.OverlayView;
    overlayProjection: google.maps.MapCanvasProjection;

    protected className = "geofenceOverlay";
    protected previousClassname: string;

    visible = false;
    protected descriptionLines: string[];
    protected longestLine: string;
    protected backgroundColor: string;
    protected strokeStyle: string;
    protected isReadable = false;
    protected hover = false;

    protected canvas: HTMLCanvasElement = null;
    protected div: HTMLDivElement = null;
    protected context: CanvasRenderingContext2D;
    protected previousSize: IGeofenceOverlaySize = {};


    constructor(
        public geoArea: IGeoArea,
        protected readonly map: google.maps.Map,
        protected readonly showLabelWhenZoomedOut: boolean,
        protected readonly drawOnAdd = false) {

        performance.mark("GCreation");

        this.canvas = null;
        this.div = null;

        this.init();
        performance.measure("GCreation");
    }

    init() {
        if (this.geoArea.GeofenceType === GeofenceType.Circle) {
            const location = new google.maps.LatLng(this.geoArea.Latitude, this.geoArea.Longitude);
            this.bounds = this.getBoundingBoxForCircle(location, this.geoArea.Radius);
        }
        else {
            this.bounds = this.getBoundingBoxForPolygonArray(this.geoArea.Points);
        }

        if (this.drawOnAdd) {
            this.showElement();
        }
    }

    showElement() {
        if (!this.div) {
            this.initOverlay();

            this.createElements();
        }

        if (this.div && !this.visible) {
            this.visible = true;
            this.overlay.setMap(this.map);
        }
    }

    drawGeofence(forceRedraw = false) {

        if (!this.overlay || !this.overlay.getMap()) {
            return;
        }

        window.performance.mark("drawGeofence");

        const hasResized = this.resizeContainingElement();
        if (!forceRedraw && !hasResized) {
            return;
        }

        if (forceRedraw) {
            this.context.clearRect(0, 0, this.previousSize.width, this.previousSize.height);
        }

        this.context.strokeStyle = this.strokeStyle;
        this.context.lineWidth = this.hover ? 3 : 1;
        this.context.fillStyle = this.backgroundColor;

        this.setGeofenceClass();

        if (this.geoArea.GeofenceType === GeofenceType.Circle) {
            this.drawCircleGeofence(this.isReadable);
        }
        else {
            this.drawPolygonGeofence(this.isReadable);
        }

        window.performance.measure("drawGeofence");
    }

    removeChild() {
        angular.element(this.div).remove();
    }

    updateColoursForMapType() {
        if (this.backgroundColor && this.strokeStyle) {
            return;
        }

        const defaultStrokeStyle = "rgba(50, 50, 50, 0.8)";
        const defaultBackgroundColor = "rgba(50, 50, 50, 0.4)";
        const satelliteBackgroundColor = "rgba(250, 250, 250, 0.4)";

        if (this.map.getMapTypeId() === google.maps.MapTypeId.SATELLITE || this.map.getMapTypeId() === google.maps.MapTypeId.HYBRID) {
            this.backgroundColor = satelliteBackgroundColor;
            this.strokeStyle = defaultStrokeStyle;
        }
        else {
            this.backgroundColor = defaultBackgroundColor;
            this.strokeStyle = defaultStrokeStyle;
        }

    }

    setGeofenceClass(forceShowLabel: boolean = false) {
        const showLabel = (forceShowLabel || this.showLabelWhenZoomedOut) && !this.isReadable;

        let className = this.className;

        if (this.hover) {
            className += " hover";
        }

        if (showLabel) {
            className += ` showLabel`;

            if (forceShowLabel) {
                className += ` force`;
            }
        }

        if (className != this.previousClassname) {
            this.div.className = className;
            this.previousClassname = className;
        }
    }

    hideElement() {
        if (this.div && this.visible) {
            this.visible = false;
            this.overlay.setMap(null);
        }
    }

    setHover(hover: boolean) {
        if (this.hover === hover) {
            return;
        }

        this.hover = hover;

        this.updateColoursForMapType();

        this.hideElement();
        this.showElement();

        if (this.visible) {

            // Quick hack to clear canvas
            this.canvas.width = this.canvas.width;
            this.drawGeofence(true);
        }
    }

    protected drawPolygonGeofence(drawText: boolean) {
        const totalWidth = this.bounds.getNorthEast().lng() - this.bounds.getSouthWest().lng();
        const totalHeight = this.bounds.getNorthEast().lat() - this.bounds.getSouthWest().lat();

        this.context.beginPath();
        for (let i = 0; i < this.geoArea.Points.length; i++) {
            const pixel = this.convertLatLngToPixelCoordinates(this.geoArea.Points[i], totalWidth, totalHeight);

            this.context.lineTo(pixel[0], pixel[1]);
        }

        this.context.stroke();
        this.context.fill();

        if (drawText) {
            const middleLng = (this.bounds.getNorthEast().lng() + this.bounds.getSouthWest().lng()) / 2;

            const pixel = this.convertLatLngToPixelCoordinates([this.geoArea.Latitude, middleLng], totalWidth, totalHeight);
            this.fillText(pixel[0], pixel[1]);
        }
    }

    protected convertLatLngToPixelCoordinates(p: [number, number], totalWidth: number, totalHeight: number) {
        const mappedXpercent = (p[1] - this.bounds.getSouthWest().lng()) / totalWidth;
        const mappedYpercent = (p[0] - this.bounds.getSouthWest().lat()) / totalHeight;

        // Whole numbers are far faster for the browser to render
        const pixelX = Math.round(this.previousSize.width * mappedXpercent);
        const pixelY = Math.round(this.previousSize.height - (this.previousSize.height * mappedYpercent));

        return [pixelX, pixelY];
    }

    private initOverlay() {
        this.overlay = new google.maps.OverlayView();

        this.overlay.onAdd = () => {
            this.addOverlayElements();

            if (this.drawOnAdd) {
                this.showElement();
                this.drawGeofence();
            }
        };

        this.overlay.draw = () => {
            this.drawGeofence();
        }

        this.overlay.onRemove = () => {
            this.removeChild();
        }
    }

    private addOverlayElements() {
        if (!this.div) {
            this.createElements();
        }

        const panes = this.overlay.getPanes();
        panes.overlayLayer.appendChild(this.div);

        // We use the south-west and north-east coordinates of the overlay to peg it to the correct position and size.
        // To do this, we need to retrieve the projection from the overlay.
        this.overlayProjection = this.overlay.getProjection();

        this.updateColoursForMapType();
    }

    private createElements() {

        window.performance.mark("createElements");
        this.div = document.createElement("div") as HTMLDivElement;
        this.div.className = this.className;

        if (this.showLabelWhenZoomedOut) {
            const textElement = document.createElement("div");
            textElement.className = "description";
            textElement.textContent = "";

            if (this.descriptionLines) {
                this.descriptionLines.forEach(line => {
                    textElement.innerHTML += `${line}<br />`;
                });
            }

            this.div.appendChild(textElement);
        }

        this.canvas = document.createElement("canvas") as HTMLCanvasElement;
        this.context = this.canvas.getContext("2d");

        this.div.appendChild(this.canvas);

        window.performance.measure("createElements");
    }

    private getBoundingBoxForCircle(point: google.maps.LatLng, radius: number) {
        const north = google.maps.geometry.spherical.computeOffset(point, radius, 0);
        const east = google.maps.geometry.spherical.computeOffset(point, radius, 90);
        const south = google.maps.geometry.spherical.computeOffset(point, radius, 180);
        const west = google.maps.geometry.spherical.computeOffset(point, radius, 270);

        const bounds = new google.maps.LatLngBounds();

        bounds.extend(north);
        bounds.extend(east);
        bounds.extend(south);
        bounds.extend(west);

        return bounds;
    }

    private getBoundingBoxForPolygonArray(polygon: [number, number][]) {
        const bounds = new google.maps.LatLngBounds();

        for (let i = 0; i < polygon.length; i++) {
            const latLng = new google.maps.LatLng(polygon[i][0], polygon[i][1]);
            bounds.extend(latLng);
        }

        return bounds;
    }

    private resizeContainingElement(): boolean {
        const size = this.getOverlaySize();

        if (!size || !size.sw || !size.ne) {
            return false;
        }

        if (this.previousSize.ne &&
            this.previousSize.sw &&
            this.previousSize.ne.equals(size.ne) &&
            this.previousSize.sw.equals(size.sw)) {
            return false;
        }

        this.div.style.left = size.sw.x + "px";
        this.div.style.top = size.ne.y + "px";

        this.previousSize.ne = size.ne;
        this.previousSize.sw = size.sw;

        if (size.width == this.previousSize.width && size.height == this.previousSize.height) {
            return false;
        }

        this.div.style.width = size.width + "px";
        this.div.style.height = size.height + "px";

        this.canvas.width = size.width;
        this.canvas.height = size.height;

        this.previousSize = size;

        this.isReadable = this.isGeofenceTextReadable();

        return true;
    }

    private drawCircleGeofence(drawText: boolean) {
        if (this.previousSize.width <= this.context.lineWidth * 2) {
            return;
        }

        const centerX = this.previousSize.width / 2;
        const centerY = this.previousSize.height / 2;

        // Check we're trying to draw a valid circle.
        const radius = centerX - Math.ceil(this.context.lineWidth / 4);

        if (radius <= 0) {
            console.warn("no radius", radius, centerX, this.context.lineWidth, this.previousSize.width, this.geoArea);
            return;
        }

        this.drawCircle(centerX, centerY, radius);

        if (drawText) {
            this.fillText(centerX, centerY);
        }
    }

    private drawCircle(x: number, y: number, radius: number, backgroundColor: string = this.backgroundColor) {
        window.performance.mark("drawCircle");

        this.context.beginPath();
        this.context.arc(x, y, radius, 0, 2 * Math.PI, false);
        this.context.closePath();
        this.context.fillStyle = (backgroundColor) ? backgroundColor : "rgba(250, 250, 250, 0.4)";

        this.context.stroke();
        this.context.fill();


        window.performance.measure("drawCircle");
    }

    private fillText(x: number, y: number) {
        window.performance.mark("fillText");

        this.context.fillStyle = "black";
        this.context.textAlign = "center";

        const fontSize = this.getFontSizeForLine(this.longestLine);
        this.context.font = `${fontSize}px Roboto`;

        if (this.descriptionLines.length === 1) {
            this.context.textBaseline = "middle";
            this.context.fillText(this.descriptionLines[0], x, y);
        }
        else {
            this.context.textBaseline = "bottom";
            this.context.fillText(this.descriptionLines[0], x, y);
            this.context.textBaseline = "top";
            this.context.fillText(this.descriptionLines[1], x, y);
        }

        window.performance.measure("fillText");
    }

    private getFontSizeForLine(line: string) {
        if (!line) {
            return 0;
        }

        window.performance.mark("getFontSizeForLine");

        const fractionModifier = line.length < 5 ? 1 : 1.2;
        const letterFraction = fractionModifier / line.length;
        let pixelSize = Math.min(this.previousSize.width, this.previousSize.height) * letterFraction;

        // Makes wider polygons text bigger, works best on boxes rather than irregular polygon
        const widthModifier = 0.75;
        if (this.previousSize.width * widthModifier > this.previousSize.height) {
            pixelSize *= this.previousSize.width * widthModifier / this.previousSize.height;
        }

        // Set max size
        pixelSize = Math.min(pixelSize, 60);

        window.performance.measure("getFontSizeForLine");

        return pixelSize;
    }

    private isGeofenceTextReadable() {
        const fontSize = this.getFontSizeForLine(this.longestLine);
        return fontSize >= 7;
    }
    getOverlaySize(): IGeofenceOverlaySize {
        if (!this.overlayProjection) {
            return {};
        }

        const sw = this.overlayProjection.fromLatLngToDivPixel(this.bounds.getSouthWest());
        const ne = this.overlayProjection.fromLatLngToDivPixel(this.bounds.getNorthEast());

        const width = Math.round(ne.x - sw.x);
        const height = Math.round(sw.y - ne.y);

        return {
            sw,
            ne,
            width,
            height
        }
    }
}
